import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, KeyRound, CheckCircle2 } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import { Logo } from "../components/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDetail(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ResetPassword() {
  usePageMeta({ title: "Reset Password | Digsby Early Learning" });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/reset-password`, { token, new_password: password });
      toast.success(data.message || "Password reset.");
      setDone(true);
      setTimeout(() => navigate("/admin"), 2000);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFBF2] px-4">
      <div className="w-full max-w-md bg-white rounded-[2rem] border-2 border-black/10 shadow-sm p-8 sm:p-10" data-testid="reset-password-card">
        <div className="flex justify-center mb-6"><Logo height={70} /></div>
        {!token ? (
          <div className="text-center">
            <p className="text-slate-700 font-medium mb-4">This reset link is missing its token.</p>
            <Link to="/admin" className="text-[#0000FF] font-bold underline">Back to login</Link>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 size={48} className="text-[#00A000] mx-auto mb-4" />
            <h1 className="font-heading font-bold text-2xl mb-2">Password reset!</h1>
            <p className="text-slate-600">Redirecting you to the login page…</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-password-form">
            <div className="flex items-center gap-2 justify-center mb-6">
              <KeyRound size={18} className="text-[#0000FF]" />
              <h1 className="font-heading font-bold text-2xl">Set a new password</h1>
            </div>
            <label htmlFor="new-password" className="block font-heading font-bold mb-2">New password</label>
            <input
              id="new-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-new-password"
              placeholder="At least 8 characters"
              className="w-full min-h-[52px] rounded-2xl border-2 border-black/15 px-5 font-medium outline-none focus:border-[#0000FF] mb-4"
            />
            <label htmlFor="confirm-password" className="block font-heading font-bold mb-2">Confirm password</label>
            <input
              id="confirm-password"
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-confirm-password"
              placeholder="Re-enter new password"
              className="w-full min-h-[52px] rounded-2xl border-2 border-black/15 px-5 font-medium outline-none focus:border-[#0000FF] mb-6"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="w-full min-h-[52px] rounded-full bg-[#FF0000] text-white font-bold hover:bg-[#d60000] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              <Lock size={16} /> {loading ? "Resetting…" : "Reset Password"}
            </button>
            <div className="text-center mt-4">
              <Link to="/admin" data-testid="reset-back-login" className="text-sm text-slate-500 hover:text-[#0000FF] font-medium">Back to login</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
